import { useState } from "react";

import { useSetNpcPriceByName } from "../api/queries";
import { ErrorBand } from "./ErrorBand";

/** Pricing a material the market has never listed, from the recipe tree.
 *
 *  The item page is still where a vendor price is maintained — this is the one
 *  place it can be *started*, because a name no scan has seen has no item row,
 *  no id and therefore no page to press a button on. Keyed by name for that
 *  reason, and the server mints the item.
 *
 *  Collapsed to a button until asked for: the tree is a table of costs, and a
 *  number input on every unscanned row would read as something the page wants
 *  filled in. Once the price lands the row resolves and this disappears on its
 *  own, so there is no "Update" here — a correction is made on the item page
 *  the price has just brought into existence. */
export function NpcPriceInline({ name }: { name: string }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const set = useSetNpcPriceByName();

  const parsed = Number(draft);
  // Not Number.isNaN: Number("") is 0 and Number("1.5") is 1.5, and a vendor
  // charges whole adena. The same guard NpcPricePanel applies to its own input.
  const valid = Number.isInteger(parsed) && parsed > 0;

  // Closing resets the mutation as well as the draft, the way AdminPanel's
  // cancel does: the error band lives in the branch below, so a failure left
  // unreset would greet whoever opens this row next with a stale alarm.
  function close() {
    set.reset();
    setDraft("");
    setOpen(false);
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}>
        Set NPC price
      </button>
    );
  }

  return (
    <>
      <div className="strip">
        <input
          type="number"
          min="1"
          step="1"
          value={draft}
          placeholder="price in adena"
          // Named, not just "NPC price in adena": a tree can offer this on
          // several rows at once, and a screen reader would otherwise announce
          // the same label for every one of them.
          aria-label={`NPC price for ${name} in adena`}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button
          type="button"
          disabled={!valid || set.isPending}
          onClick={() => set.mutate({ name, price: parsed }, { onSuccess: close })}
        >
          Set
        </button>
        <button type="button" disabled={set.isPending} onClick={close}>
          Cancel
        </button>
      </div>
      {set.isError ? (
        <ErrorBand
          message="Could not save the price."
          hint="It may have been changed elsewhere. Reload and try again."
        />
      ) : null}
    </>
  );
}
